/**
 * worldTheme — how flat (image-less) world objects are drawn.
 *
 * A map made of plain Tiled shapes (rectangles, rotated rectangles, polygons)
 * carries no artwork; the object's `type` picks a style here. Swapping the
 * look of the whole world is editing this table — no asset pipeline, no
 * artist. When real artwork arrives later, an object with an image still wins
 * (the renderer only falls back to the theme when it has no texture).
 *
 * Style shape: { fill, stroke, strokeWidth, shadow? }
 *   shadow: { dx, dy, alpha } — a flat drop shadow that reads as height.
 */
export const DEFAULT_THEME = {
  ground: 0x5b8a4e,          // grass inside the arena walls
  groundOutside: 0x2f4a32,   // darker grass beyond the boundary walls
  groundGrid: 0x55824a,      // faint 200 px grid so movement reads on a flat field
  boundary: 0x2b2f36,        // map edge wall

  styles: {
    wall:   { fill: 0x3a3a44, stroke: 0x1c1c22, strokeWidth: 4, shadow: { dx: 10, dy: 12, alpha: 0.25 } },
    block:  { fill: 0xa8743d, stroke: 0x5c3d1c, strokeWidth: 4, shadow: { dx: 8, dy: 10, alpha: 0.25 } },
    pillar: { fill: 0x8a8f99, stroke: 0x4a4e57, strokeWidth: 4, shadow: { dx: 8, dy: 10, alpha: 0.25 } },
    // Firewall ability: translucent orange force field; fades as it takes damage.
    firewall: { fill: 0xff7a1a, stroke: 0xffc27a, strokeWidth: 3, alpha: 0.45 },
    // Anything the map author did not name: still visible, obviously placeholder.
    obstacle: { fill: 0x7a4f9e, stroke: 0x3b2450, strokeWidth: 3 }
  }
};

let theme = DEFAULT_THEME;

export function setWorldTheme(next) {
  theme = { ...DEFAULT_THEME, ...next, styles: { ...DEFAULT_THEME.styles, ...(next?.styles || {}) } };
}

export function getWorldTheme() {
  return theme;
}

/** Style for a world object; unknown types get the `obstacle` placeholder. */
export function styleFor(obj) {
  return theme.styles[obj?.type] || theme.styles.obstacle;
}

/**
 * World-space outline of a flat object: polygons use their vertices, rectangles
 * their (rotated) corners. Returns a flat [x0, y0, x1, y1, …] list.
 */
export function outlineOf(obj) {
  if (obj.isPolygon && obj.vertices?.length >= 3) {
    const pts = [];
    for (const v of obj.vertices) pts.push(obj.x + v.x, obj.y + v.y);
    return pts;
  }
  const w = obj.width || 0;
  const h = obj.height || 0;
  if (!w || !h) return null;
  const a = ((obj.rotation || 0) * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const pts = [];
  for (const [px, py] of [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]]) {
    pts.push(obj.x + px * c - py * s, obj.y + px * s + py * c);
  }
  return pts;
}

/** Draw one flat object (shadow, fill, stroke) into a PIXI.Graphics. */
export function drawFlatObject(g, obj) {
  const pts = outlineOf(obj);
  if (!pts) return false;
  const style = styleFor(obj);
  if (style.shadow) {
    const sh = [];
    for (let i = 0; i < pts.length; i += 2) sh.push(pts[i] + style.shadow.dx, pts[i + 1] + style.shadow.dy);
    g.poly(sh);
    g.fill({ color: 0x000000, alpha: style.shadow.alpha });
  }
  // Damaged objects (firewall hp/maxHp) fade towards 40 % so remaining strength is readable.
  const health = obj.maxHp > 0 ? Math.max(0, Math.min(1, obj.hp / obj.maxHp)) : 1;
  const alpha = (style.alpha ?? 1) * (0.4 + 0.6 * health);
  g.poly(pts);
  g.fill({ color: style.fill, alpha });
  g.stroke({ color: style.stroke, width: style.strokeWidth, alignment: 0.5, alpha: Math.min(1, alpha + 0.3) });
  return true;
}
